// engagement-components/Settings.jsx
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Form, Button, Spinner, InputGroup } from "react-bootstrap";
import { FaWhatsapp, FaEnvelope, FaBell, FaSave, FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";
import { useNotification } from "../../../../context/NotificationContext";

const Section = ({ title, icon, color = "#0d6efd", children }) => (
  <Card className="shadow-sm border-0 mb-4">
    <Card.Header className="bg-white border-bottom py-3">
      <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
        <span style={{ color }}>{icon}</span>{title}
      </h6>
    </Card.Header>
    <Card.Body className="p-4">{children}</Card.Body>
  </Card>
);

const Settings = () => {
  const { notify } = useNotification();
  const [saving, setSaving] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const [wa, setWa] = useState({
    senderName: localStorage.getItem("wa_sender_name") || "",
    phone: localStorage.getItem("wa_phone") || "",
    apiKey: "",
    businessId: "",
    provider: "GupShup",
  });

  const [smtp, setSmtp] = useState({
    host: "smtp.netshineoffice.com",
    port: "587",
    user: "noreply@netshineoffice.com",
    password: "",
    fromName: localStorage.getItem("companyname") || "Netshine Hospitality",
    secure: true,
    provider: "netshine",
  });

  const [notif, setNotif] = useState({
    emailOnCampaignComplete: true,
    emailOnJourneyFire: false,
    emailOnConversion: true,
    alertEmail: localStorage.getItem("alert_email") || "",
  });

  // Load saved settings from API on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [waRes, smtpRes, notifRes] = await Promise.all([
          axios.get(`${apiUrl}/feedback/settings/whatsapp`,     authHeaders()),
          axios.get(`${apiUrl}/feedback/settings/smtp`,         authHeaders()),
          axios.get(`${apiUrl}/feedback/settings/notifications`,authHeaders()),
        ]);
        if (waRes.data   && Object.keys(waRes.data).length)   setWa(w   => ({ ...w,    ...waRes.data   }));
        if (smtpRes.data && Object.keys(smtpRes.data).length) setSmtp(s => ({ ...s,    ...smtpRes.data }));
        if (notifRes.data && Object.keys(notifRes.data).length) setNotif(n => ({ ...n, ...notifRes.data }));
      } catch { /* use defaults from localStorage */ }
    };
    load();
  }, []);

  const saveWA = async () => {
    setSaving("wa");
    localStorage.setItem("wa_sender_name", wa.senderName);
    localStorage.setItem("wa_phone", wa.phone);
    try {
      await axios.post(`${apiUrl}/feedback/settings/whatsapp`, wa, authHeaders());
      notify("success", "WhatsApp settings saved.");
    } catch { notify("error", "Failed to save WhatsApp settings."); }
    setSaving("");
  };

  const saveSMTP = async () => {
    setSaving("smtp");
    try {
      await axios.post(`${apiUrl}/feedback/settings/smtp`, smtp, authHeaders());
      notify("success", "Email settings saved.");
    } catch { notify("error", "Failed to save email settings."); }
    setSaving("");
  };

  const testSMTP = async () => {
    if (!testEmail) { notify("error", "Enter a test email first."); return; }
    setTesting(true);
    try {
      await axios.post(`${apiUrl}/feedback/settings/smtp/test`, { to: testEmail, ...smtp }, authHeaders());
      notify("success", `Test email sent to ${testEmail}`);
    } catch { notify("error", "Test email failed — check SMTP settings."); }
    setTesting(false);
  };

  const saveNotif = async () => {
    setSaving("notif");
    localStorage.setItem("alert_email", notif.alertEmail);
    try {
      await axios.post(`${apiUrl}/feedback/settings/notifications`, notif, authHeaders());
      notify("success", "Notification preferences saved.");
    } catch { notify("error", "Failed to save notification settings."); }
    setSaving("");
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h3 className="fw-bold text-dark mb-0"><i className="fas fa-cog text-primary me-2"></i>Platform Settings</h3>
          <p className="text-muted mb-0 mt-1">Configure WhatsApp sender, email, and notification preferences</p>
        </div>
      </div>

      <Section title="WhatsApp Sender" icon={<FaWhatsapp />} color="#198754">
        <div className="alert alert-success py-2 mb-4" style={{ fontSize: "0.82rem" }}>
          <FaWhatsapp className="me-2" />
          {wa.provider === "Netshine"
            ? "Using Netshine WhatsApp sending — no additional setup required."
            : `Your WhatsApp Business number must be whitelisted with ${wa.provider} before messages can be sent.`}
        </div>
        <Row className="g-3">
          <Col md={4}>
            <Form.Label className="fw-semibold">Provider</Form.Label>
            <Form.Select value={wa.provider} onChange={(e) => setWa(p => ({ ...p, provider: e.target.value }))}>
              {["Netshine","GupShup","Twilio","360dialog","Interakt","Wati"].map(p => <option key={p}>{p}</option>)}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label className="fw-semibold">Sender Name <span className="text-danger">*</span></Form.Label>
            <Form.Control value={wa.senderName} onChange={(e) => setWa(p => ({ ...p, senderName: e.target.value }))}
              placeholder="e.g. Grand Hotel" />
            <Form.Text className="text-muted">
              {wa.provider === "Netshine" ? "Your hotel name shown to guests" : `Must match approved name in ${wa.provider}`}
            </Form.Text>
          </Col>
          <Col md={4}>
            <Form.Label className="fw-semibold">Whitelisted Phone <span className="text-danger">*</span></Form.Label>
            <InputGroup>
              <InputGroup.Text><FaWhatsapp className="text-success" /></InputGroup.Text>
              <Form.Control value={wa.phone} onChange={(e) => setWa(p => ({ ...p, phone: e.target.value }))}
                placeholder="+91 98765 43210" />
            </InputGroup>
          </Col>
          {wa.provider !== "Netshine" && (
            <>
              <Col md={6}>
                <Form.Label className="fw-semibold">
                  {wa.provider === "GupShup"  ? "GupShup API Key" :
                   wa.provider === "Twilio"   ? "Twilio Auth Token" :
                   wa.provider === "360dialog" ? "360dialog API Key" :
                   wa.provider === "Interakt" ? "Interakt API Token" :
                   wa.provider === "Wati"     ? "Wati API Endpoint Token" : "API Key"}
                </Form.Label>
                <InputGroup>
                  <Form.Control type={showPass ? "text" : "password"} value={wa.apiKey}
                    onChange={(e) => setWa(p => ({ ...p, apiKey: e.target.value }))}
                    placeholder="Enter API key..." />
                  <Button variant="outline-secondary" onClick={() => setShowPass(!showPass)}>
                    {showPass ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </InputGroup>
              </Col>
              <Col md={6}>
                <Form.Label className="fw-semibold">
                  {wa.provider === "GupShup"  ? "Business App ID" :
                   wa.provider === "Twilio"   ? "Account SID" :
                   wa.provider === "360dialog" ? "Channel ID" :
                   wa.provider === "Interakt" ? "Business Phone Number ID" :
                   wa.provider === "Wati"     ? "Wati API Endpoint URL" : "Business ID"}
                </Form.Label>
                <Form.Control value={wa.businessId} onChange={(e) => setWa(p => ({ ...p, businessId: e.target.value }))}
                  placeholder={
                    wa.provider === "Twilio" ? "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" :
                    wa.provider === "Wati"   ? "https://live-mt-server.wati.io/xxxxx" :
                    "Enter ID..."
                  } />
              </Col>
            </>
          )}
        </Row>
        <div className="d-flex justify-content-end mt-4">
          <Button variant="success" onClick={saveWA} disabled={saving === "wa"} className="px-4">
            {saving === "wa" ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : <><FaSave className="me-2" />Save WhatsApp Settings</>}
          </Button>
        </div>
      </Section>

      <Section title="Email (SMTP)" icon={<FaEnvelope />} color="#0d6efd">
        <div className="mb-3 d-flex gap-2">
          {["netshine", "custom"].map((p) => (
            <Button key={p} size="sm" variant={smtp.provider === p ? "primary" : "outline-secondary"} className="rounded-pill"
              onClick={() => setSmtp(prev => ({ ...prev, provider: p, ...(p === "netshine" ? { host: "smtp.netshineoffice.com", port: "587", user: "noreply@netshineoffice.com" } : {}) }))}>
              {p === "netshine" ? "Netshine Default (Recommended)" : "Custom SMTP"}
            </Button>
          ))}
        </div>
        {smtp.provider === "netshine" && (
          <div className="alert alert-info py-2 mb-3" style={{ fontSize: "0.82rem" }}>
            Using Netshine shared sending. Emails sent from <strong>noreply@netshineoffice.com</strong> with your hotel name.
          </div>
        )}
        <Row className="g-3">
          <Col md={6}>
            <Form.Label className="fw-semibold">Sender Display Name</Form.Label>
            <Form.Control value={smtp.fromName} onChange={(e) => setSmtp(p => ({ ...p, fromName: e.target.value }))} placeholder="Grand Hotel" />
          </Col>
          <Col md={6}>
            <Form.Label className="fw-semibold">Sender Email</Form.Label>
            <Form.Control value={smtp.user} onChange={(e) => setSmtp(p => ({ ...p, user: e.target.value }))} disabled={smtp.provider === "netshine"} placeholder="noreply@yourhotel.com" />
          </Col>
          {smtp.provider === "custom" && (
            <>
              <Col md={5}><Form.Label className="fw-semibold">SMTP Host</Form.Label><Form.Control value={smtp.host} onChange={(e) => setSmtp(p => ({ ...p, host: e.target.value }))} placeholder="smtp.gmail.com" /></Col>
              <Col md={2}><Form.Label className="fw-semibold">Port</Form.Label><Form.Control value={smtp.port} onChange={(e) => setSmtp(p => ({ ...p, port: e.target.value }))} placeholder="587" /></Col>
              <Col md={5}>
                <Form.Label className="fw-semibold">Password</Form.Label>
                <InputGroup>
                  <Form.Control type={showPass ? "text" : "password"} value={smtp.password} onChange={(e) => setSmtp(p => ({ ...p, password: e.target.value }))} />
                  <Button variant="outline-secondary" onClick={() => setShowPass(!showPass)}>{showPass ? <FaEyeSlash /> : <FaEye />}</Button>
                </InputGroup>
              </Col>
            </>
          )}
        </Row>
        <div className="mt-4 p-3 rounded border" style={{ background: "#f8f9fb" }}>
          <Form.Label className="fw-semibold small">Send Test Email</Form.Label>
          <InputGroup style={{ maxWidth: 400 }}>
            <Form.Control value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" type="email" />
            <Button variant="outline-primary" onClick={testSMTP} disabled={testing}>{testing ? <Spinner size="sm" animation="border" className="me-1" /> : null}Send Test</Button>
          </InputGroup>
        </div>
        <div className="d-flex justify-content-end mt-4">
          <Button variant="primary" onClick={saveSMTP} disabled={saving === "smtp"} className="px-4">
            {saving === "smtp" ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : <><FaSave className="me-2" />Save Email Settings</>}
          </Button>
        </div>
      </Section>

      <Section title="Alert Notifications" icon={<FaBell />} color="#ffc107">
        <Row className="g-3">
          <Col md={6}>
            <Form.Label className="fw-semibold">Alert Email Address</Form.Label>
            <Form.Control value={notif.alertEmail} onChange={(e) => setNotif(p => ({ ...p, alertEmail: e.target.value }))} type="email" placeholder="manager@yourhotel.com" />
          </Col>
        </Row>
        <div className="mt-3">
          {[
            { key: "emailOnCampaignComplete", label: "Notify when a campaign finishes sending" },
            { key: "emailOnJourneyFire",      label: "Notify when a journey step fires"        },
            { key: "emailOnConversion",       label: "Notify on each new conversion"            },
          ].map((item) => (
            <Form.Check key={item.key} type="switch" id={`notif-${item.key}`} label={item.label} className="mb-2"
              checked={notif[item.key]} onChange={(e) => setNotif(p => ({ ...p, [item.key]: e.target.checked }))} />
          ))}
        </div>
        <div className="d-flex justify-content-end mt-4">
          <Button variant="warning" onClick={saveNotif} disabled={saving === "notif"} className="px-4">
            {saving === "notif" ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : <><FaSave className="me-2" />Save Notification Settings</>}
          </Button>
        </div>
      </Section>
      <style>{`.card{border-radius:12px!important}.card-header{border-radius:12px 12px 0 0!important}`}</style>
    </div>
  );
};

export default Settings;
